import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Combobox, useToast } from '@keobook/ui';
import { api } from '../lib/api';

interface CoaAccount {
  id: string;
  code: string;
  name: string;
  kind: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  isArchived?: boolean;
}

/**
 * Single-shot picker dialog: anchor a button on the existing account chip,
 * click it, get a Combobox with every CoA account, pick a new one, submit.
 * Reuses the searchable Combobox from @keobook/ui so typing "uncat"
 * surfaces "7900 Uncategorized Expense", typing "legal" surfaces "6400
 * Legal & Professional", etc.
 *
 * Two callable shapes:
 *   - mode='proposal'  → calls PATCH /v1/proposals/:id   (debit or credit side)
 *   - mode='ledger'    → calls PATCH /v1/ledger/entries/:entryId/lines/:lineId
 */

type ReclassifyTarget =
  | {
      mode: 'proposal';
      proposalId: string;
      side: 'debit' | 'credit';
    }
  | {
      mode: 'ledger';
      entryId: string;
      lineId: string;
    };

export function CoaPicker({
  currentCode,
  currentLabel,
  target,
  onDone,
  disabled,
}: {
  currentCode: string;
  /** Visible label for the chip — falls back to the code if undefined. */
  currentLabel?: string;
  target: ReclassifyTarget;
  /** Fires after a successful save with the chosen account code. */
  onDone: (chosen: { code: string; name: string; id?: string }) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<CoaAccount[] | null>(null);
  const [pickedId, setPickedId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const chipRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open || accounts !== null) return;
    let alive = true;
    void api
      .getAccounts()
      .then((r) => {
        if (!alive) return;
        const sorted = [...r.accounts]
          .filter((a) => !a.isArchived)
          .sort((a, b) => a.code.localeCompare(b.code));
        setAccounts(sorted as CoaAccount[]);
        // Seed the picker with whatever the line is currently using so the
        // "selected" highlight reflects the user's starting point.
        const current = sorted.find((a) => a.code === currentCode);
        setPickedId(current?.id ?? '');
      })
      .catch((err: unknown) => {
        toast({
          tone: 'error',
          message: err instanceof Error ? err.message : 'Could not load chart of accounts.',
        });
        setOpen(false);
      });
    return () => {
      alive = false;
    };
  }, [open, accounts, currentCode, toast]);

  const options = useMemo(
    () =>
      (accounts ?? []).map((a) => ({
        value: a.id,
        // Single-line label: "6400 — Legal & Professional". Combobox
        // matches substring on this string, so codes and names are both
        // searchable.
        label: `${a.code} — ${a.name}`,
      })),
    [accounts],
  );

  async function submit() {
    if (!pickedId || pickedId === pickedCurrentId(accounts, currentCode)) {
      // Nothing to do.
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      const chosen = accounts!.find((a) => a.id === pickedId)!;
      if (target.mode === 'proposal') {
        await api.editProposal(target.proposalId, {
          [target.side === 'debit' ? 'debitAccountCode' : 'creditAccountCode']: chosen.code,
        });
        toast({ tone: 'success', message: `Reclassified to ${chosen.code} ${chosen.name}.` });
      } else {
        await api.reclassifyLine(target.entryId, target.lineId, chosen.id);
        toast({ tone: 'success', message: `Moved to ${chosen.code} ${chosen.name}.` });
      }
      onDone({ id: chosen.id, code: chosen.code, name: chosen.name });
      setOpen(false);
    } catch (err: unknown) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not reclassify.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        className="kb-coapicker__chip"
        disabled={disabled}
        onClick={() => setOpen(true)}
        title="Reclassify"
      >
        <span className="kb-report__code">{currentCode}</span> {currentLabel ?? ''}
        <span className="kb-coapicker__edit" aria-hidden="true">
          ✎
        </span>
      </button>
      {open &&
        createPortal(
          <div className="kb-modal-overlay" onClick={() => !busy && setOpen(false)}>
            <div
              className="kb-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="kb-coapicker-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="kb-coapicker-title" className="kb-modal__title">
                Reclassify to a different account
              </h2>
              <div className="kb-modal__body">
                <p style={{ marginTop: 0 }}>
                  Currently: <strong>{currentCode}</strong>
                  {currentLabel ? ` — ${currentLabel}` : null}
                </p>
                {accounts === null ? (
                  <p className="kb-muted">Loading chart of accounts…</p>
                ) : (
                  <Combobox
                    options={options}
                    value={pickedId}
                    onChange={(v) => setPickedId(v)}
                    placeholder="Search by code or name…"
                    width="100%"
                  />
                )}
              </div>
              <div className="kb-modal__actions">
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={
                    busy || !pickedId || pickedId === pickedCurrentId(accounts, currentCode)
                  }
                  onClick={() => void submit()}
                >
                  {busy ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function pickedCurrentId(accounts: CoaAccount[] | null, code: string): string {
  return accounts?.find((a) => a.code === code)?.id ?? '';
}
